/*
 * File: index.tsx
 * Desc: 表单输入组件
 * File Created: 2019-10-09 10:51:25
 * Author: chenghao at <hao.cheng@karakal.com.cn>
 * ------
 * Copyright 2019 - present, karakal
 */
import React from 'react';
import { Form, Input } from 'antd';
import { FormProps, WrappedFormUtils } from 'antd/lib/form/Form';
import { PrototypeCheck } from '../utils';
import classNames from 'classnames';
import FormTextareaHint from '../form-textarea-hint';

export interface FormBaseProps {
    /** 字段的英文key */
    field: string;
    /** 字段的中文名 */
    label?: string;
    /** 默认值 */
    initialValue?: any;
    /** 是否只读 */
    readOnly?: boolean;
    /** 是否不可编辑 */
    disabled?: boolean;
    /** 是否显示删除按钮 */
    allowClear?: boolean;
    /** 是否必填 */
    required?: boolean;
    /** 占位提示字符串 */
    placeholder?: string;
    /** 自定义校验函数 */
    validator?: Function;
    /** 自定义详情渲染组件 */
    render?: (value: any, form?: WrappedFormUtils) => React.ReactElement;
}
interface FormInputProps extends FormBaseProps, FormProps {
    max?: number;
    validator?: (rule: any, value: any, callback: any, form: any) => any;
    /** 数据过滤函数 */
    valueFilter?: (value: any) => any;
    /** 输入框Input组件的props */
    inputProps?: any;
    /** 输入的值类型 */
    valueType?: string;
    /** 是否显示文本框 */
    textarea?: boolean;
    autoSize?: any;
}

const FormInput = ({
    required,
    label,
    field,
    placeholder,
    max,
    readOnly,

    initialValue,
    valueFilter,
    form,
    disabled,
    inputProps,
    validator,
    valueType,
    allowClear,
    labelCol,
    wrapperCol,
    className,
    textarea,
    autoSize,
    ...restProps
}: FormInputProps) => {
    const { getFieldDecorator, getFieldValue } = form as WrappedFormUtils;
    const rules = [
        ...[
            required && {
                required,
                // message: label ? `请输入${label}` : placeholder,
                message: placeholder ? placeholder : `请输入${label}`,
                transform: (value: any) => {
                    if (value) {
                        if (PrototypeCheck.isString(value)) {
                            (value as string).trim();
                        } else if (PrototypeCheck.isNumber(value)) {
                            value = JSON.stringify(value);
                        }
                    }
                    return value;
                },
            },
        ],
        {
            validator: (rule: any, value: any, callback: Function) => {
                if (value && valueType === 'number' && !PrototypeCheck.isNumber(Number(value))) {
                    callback('请输入数字');
                }

                if (value && validator) {
                    validator(rule, value, callback, form);
                } else callback();
            },
        },
        ...(max ? [{ max, message: `不能输入超过${max}个字符` }] : []),
    ].filter(Boolean) as any;

    const commonProps = {
        placeholder: placeholder || `请输入${label}`,
        ...inputProps,
        disabled,
    };
    return (
        <Form.Item
            {...{
                label,
                labelCol,
                wrapperCol,
            }}
            className={classNames('rac_form_input', className)}
        >
            {getFieldDecorator(field, {
                initialValue,
                rules,
                validateFirst: true, //当某一规则校验不通过时，是否停止剩下的规则的校验
                normalize: (value: any) => {
                    if (valueFilter) return valueFilter(value);
                    return value;
                },
            })(
                readOnly ? (
                    restProps.render ? (
                        restProps.render(getFieldValue(field))
                    ) : (
                        <span>{getFieldValue(field)}</span>
                    )
                ) : textarea ? (
                    <div className="rac_form_textarea-container">
                        <Input.TextArea
                            {...commonProps}
                            value={getFieldValue(field)}
                            autosize={autoSize}
                        />
                        <FormTextareaHint
                            value={getFieldValue(field)}
                            maxLength={inputProps && inputProps.maxLength}
                        />
                    </div>
                ) : (
                    <Input {...commonProps} bordered={false} allowClear={allowClear} />
                )
            )}
        </Form.Item>
    );
};

export default FormInput;
